// pages/Dashboard/Finance.js
import { useState, useEffect, useCallback } from 'react';
import {
  FaPlus,
  FaSearch,
  FaTimes,
  FaArrowUp,
  FaArrowDown,
  FaWallet,
  FaTrash,
  FaEdit,
  FaFileInvoiceDollar,
  FaCalendarAlt,
} from 'react-icons/fa';
import '../Css/Finance.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const INCOME_CATEGORIES = ['Service Revenue', 'Product Sales', 'Membership', 'Other Income'];
const EXPENSE_CATEGORIES = ['Salary', 'Rent', 'Utilities', 'Supplies', 'Marketing', 'Maintenance', 'Other Expense'];
const PAYMENT_MODES = ['cash', 'bank_transfer', 'upi', 'cheque', 'card'];

const EMPTY_FORM = {
  type: 'income',
  category: '',
  amount: '',
  transactionDate: new Date().toISOString().slice(0, 10),
  paymentMode: '',
  notes: '',
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString()}`;

function Finance() {
  const [filterMode, setFilterMode] = useState('month'); // month | date | range
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [singleDate, setSingleDate] = useState(now.toISOString().slice(0, 10));
  const [fromDate, setFromDate] = useState(now.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(now.toISOString().slice(0, 10));

  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ totalIncome: 0, totalExpense: 0, netBalance: 0, incomeCount: 0, expenseCount: 0 });
  const [balanceSheet, setBalanceSheet] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showBalanceSheet, setShowBalanceSheet] = useState(false);

  const toast = useToast();

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();

    if (filterMode === 'month') {
      params.append('month', month);
      params.append('year', year);
    } else if (filterMode === 'date') {
      params.append('date', singleDate);
    } else if (filterMode === 'range') {
      params.append('fromDate', fromDate);
      params.append('toDate', toDate);
    }

    if (typeFilter !== 'all') params.append('type', typeFilter);
    if (search) params.append('search', search);

    return params;
  }, [filterMode, month, year, singleDate, fromDate, toDate, typeFilter, search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = buildFilterParams();

      const res = await fetch(`${baseurl}/finance?${params.toString()}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch transactions');

      setTransactions(data.data.transactions);
      setSummary(data.data.summary);

      // balance sheet uses date filters only (no type/search)
      const bsParams = new URLSearchParams();
      if (filterMode === 'month') {
        bsParams.append('month', month);
        bsParams.append('year', year);
      } else if (filterMode === 'date') {
        bsParams.append('date', singleDate);
      } else if (filterMode === 'range') {
        bsParams.append('fromDate', fromDate);
        bsParams.append('toDate', toDate);
      }

      const bsRes = await fetch(`${baseurl}/finance/balance-sheet?${bsParams.toString()}`, {
        headers: authHeaders(),
      });
      const bsData = await bsRes.json();
      if (bsRes.ok) setBalanceSheet(bsData.data.balanceSheet);
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to fetch transactions', 'error');
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, filterMode, month, year, singleDate, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDrawer = (type = 'income') => {
    setForm({ ...EMPTY_FORM, type });
    setEditingId(null);
    setShowDrawer(true);
  };

  const openEditDrawer = (txn) => {
    setForm({
      type: txn.type,
      category: txn.category,
      amount: txn.amount,
      transactionDate: txn.transaction_date.slice(0, 10),
      paymentMode: txn.payment_mode || '',
      notes: txn.notes || '',
    });
    setEditingId(txn.id);
    setShowDrawer(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'type' ? { category: '' } : {}),
    }));
  };

  const handleSubmit = async () => {
    if (!form.category || !form.amount || !form.transactionDate) {
      toast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        type: form.type,
        category: form.category,
        amount: Number(form.amount),
        transactionDate: form.transactionDate,
        paymentMode: form.paymentMode || null,
        notes: form.notes,
      };

      const url = editingId ? `${baseurl}/finance/${editingId}` : `${baseurl}/finance`;

      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save transaction');

      toast(data.message || 'Transaction saved successfully', 'success');
      setShowDrawer(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchData();
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to save transaction', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${baseurl}/finance/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete transaction');

      toast(data.message || 'Transaction deleted successfully', 'success');
      await fetchData();
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to delete transaction', 'error');
    }
  };

  const categoryOptions = form.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  return (
    <div className="finance-page">
      <div className="finance-header">
        <div>
          <h2>Income &amp; Expense</h2>
          <p>Track transactions and view your balance sheet</p>
        </div>
        <div className="header-actions">
          <button className="btn-outline" onClick={() => setShowBalanceSheet(true)}>
            <FaFileInvoiceDollar /> Balance Sheet
          </button>
          <button className="btn-danger-outline" onClick={() => openAddDrawer('expense')}>
            <FaArrowDown /> Add Expense
          </button>
          <button className="btn-primary" onClick={() => openAddDrawer('income')}>
            <FaArrowUp /> Add Income
          </button>
        </div>
      </div>

      {error && <div className="finance-error-banner">{error}</div>}

      {/* SUMMARY STRIP */}
      <div className="finance-stats-strip">
        <div className="finance-stat-card">
          <span className="finance-stat-value stat-income-text">{fmt(summary.totalIncome)}</span>
          <span className="finance-stat-label">Total Income ({summary.incomeCount})</span>
        </div>
        <div className="finance-stat-card">
          <span className="finance-stat-value stat-expense-text">{fmt(summary.totalExpense)}</span>
          <span className="finance-stat-label">Total Expense ({summary.expenseCount})</span>
        </div>
        <div className="finance-stat-card">
          <span className={`finance-stat-value ${summary.netBalance >= 0 ? 'stat-income-text' : 'stat-expense-text'}`}>
            {fmt(summary.netBalance)}
          </span>
          <span className="finance-stat-label">Net Balance (Period)</span>
        </div>
        <div className="finance-stat-card">
          <span className="finance-stat-value">{balanceSheet ? fmt(balanceSheet.closingBalance) : '-'}</span>
          <span className="finance-stat-label">Closing Balance</span>
        </div>
      </div>

      {/* FILTERS */}
      <div className="finance-filter-bar">
        <div className="filter-mode-chips">
          {['month', 'date', 'range'].map((m) => (
            <button
              key={m}
              className={`filter-chip ${filterMode === m ? 'active' : ''}`}
              onClick={() => setFilterMode(m)}
            >
              {m === 'month' ? 'Monthly' : m === 'date' ? 'Single Date' : 'Date Range'}
            </button>
          ))}
        </div>

        <div className="filter-inputs">
          {filterMode === 'month' && (
            <>
              <select className="form-select-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
              <input
                type="number"
                className="form-input-sm"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                style={{ width: 90 }}
              />
            </>
          )}

          {filterMode === 'date' && (
            <input
              type="date"
              className="form-input-sm"
              value={singleDate}
              onChange={(e) => setSingleDate(e.target.value)}
            />
          )}

          {filterMode === 'range' && (
            <>
              <input
                type="date"
                className="form-input-sm"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <span className="range-sep">to</span>
              <input
                type="date"
                className="form-input-sm"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </>
          )}
        </div>

        <div className="filter-chips">
          {['all', 'income', 'expense'].map((t) => (
            <button
              key={t}
              className={`filter-chip ${typeFilter === t ? 'active' : ''}`}
              onClick={() => setTypeFilter(t)}
            >
              {t === 'all' ? 'All' : t === 'income' ? 'Income' : 'Expense'}
            </button>
          ))}
        </div>

        <div className="input-group search-input">
          <FaSearch className="input-icon" />
          <input
            type="text"
            placeholder="Search category or notes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* TRANSACTIONS TABLE */}
      <div className="finance-table-wrapper">
        <table className="finance-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Category</th>
              <th>Payment Mode</th>
              <th>Notes</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="7" className="finance-empty-row">Loading transactions...</td>
              </tr>
            )}

            {!loading &&
              transactions.map((txn) => (
                <tr key={txn.id} className="finance-row">
                  <td>
                    <span className="table-date">
                      <FaCalendarAlt /> {new Date(txn.transaction_date).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <span className={`type-pill ${txn.type === 'income' ? 'type-income' : 'type-expense'}`}>
                      {txn.type === 'income' ? <FaArrowUp /> : <FaArrowDown />}
                      {txn.type === 'income' ? 'Income' : 'Expense'}
                    </span>
                  </td>
                  <td>{txn.category}</td>
                  <td>{txn.payment_mode ? txn.payment_mode.replace('_', ' ') : '-'}</td>
                  <td className="notes-cell">{txn.notes || '-'}</td>
                  <td>
                    <strong className={txn.type === 'income' ? 'stat-income-text' : 'stat-expense-text'}>
                      {txn.type === 'income' ? '+' : '-'}{fmt(txn.amount)}
                    </strong>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="icon-btn" onClick={() => openEditDrawer(txn)}>
                        <FaEdit />
                      </button>
                      <button className="icon-btn danger" onClick={() => handleDelete(txn.id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

            {!loading && transactions.length === 0 && (
              <tr>
                <td colSpan="7" className="finance-empty-row">No transactions found for this period</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ADD / EDIT DRAWER */}
      {showDrawer && (
        <div className="drawer-overlay" onClick={() => setShowDrawer(false)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className={`finance-avatar ${form.type === 'income' ? 'avatar-income' : 'avatar-expense'}`}>
                  {form.type === 'income' ? <FaArrowUp /> : <FaArrowDown />}
                </div>
                <div>
                  <h3>{editingId ? 'Edit Transaction' : 'Add Transaction'}</h3>
                  <span className="table-sub">Record income or expense</span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setShowDrawer(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-section">
                <label className="form-label">Type</label>
                <div className="type-toggle">
                  <button
                    type="button"
                    className={`type-toggle-btn ${form.type === 'income' ? 'active-income' : ''}`}
                    onClick={() => handleFormChange('type', 'income')}
                  >
                    <FaArrowUp /> Income
                  </button>
                  <button
                    type="button"
                    className={`type-toggle-btn ${form.type === 'expense' ? 'active-expense' : ''}`}
                    onClick={() => handleFormChange('type', 'expense')}
                  >
                    <FaArrowDown /> Expense
                  </button>
                </div>
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={form.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                  >
                    <option value="">Select category</option>
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Amount (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 5000"
                    value={form.amount}
                    onChange={(e) => handleFormChange('amount', e.target.value)}
                  />
                </div>
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.transactionDate}
                    onChange={(e) => handleFormChange('transactionDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label">Payment Mode</label>
                  <select
                    className="form-select"
                    value={form.paymentMode}
                    onChange={(e) => handleFormChange('paymentMode', e.target.value)}
                  >
                    <option value="">Select mode</option>
                    {PAYMENT_MODES.map((m) => (
                      <option key={m} value={m}>{m.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="drawer-section">
                <label className="form-label">Notes (optional)</label>
                <textarea
                  className="form-textarea"
                  rows="3"
                  placeholder="Add a note"
                  value={form.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn-outline full-width" onClick={() => setShowDrawer(false)}>
                Cancel
              </button>
              <button className="btn-primary full-width" onClick={handleSubmit} disabled={saving}>
                <FaPlus /> {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BALANCE SHEET DRAWER */}
      {showBalanceSheet && (
        <div className="drawer-overlay" onClick={() => setShowBalanceSheet(false)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className="finance-avatar avatar-balance">
                  <FaWallet />
                </div>
                <div>
                  <h3>Balance Sheet</h3>
                  <span className="table-sub">
                    {filterMode === 'month'
                      ? `${new Date(0, month - 1).toLocaleString('default', { month: 'long' })} ${year}`
                      : filterMode === 'date'
                      ? new Date(singleDate).toLocaleDateString()
                      : `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`}
                  </span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setShowBalanceSheet(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              {balanceSheet ? (
                <>
                  <div className="balance-sheet-row">
                    <span>Opening Balance</span>
                    <strong>{fmt(balanceSheet.openingBalance)}</strong>
                  </div>
                  <div className="balance-sheet-row income">
                    <span>+ Total Income</span>
                    <strong>{fmt(balanceSheet.totalIncome)}</strong>
                  </div>
                  <div className="balance-sheet-row expense">
                    <span>- Total Expense</span>
                    <strong>{fmt(balanceSheet.totalExpense)}</strong>
                  </div>
                  <div className="balance-sheet-divider"></div>
                  <div className="balance-sheet-row closing">
                    <span>Closing Balance</span>
                    <strong>{fmt(balanceSheet.closingBalance)}</strong>
                  </div>

                  <div className="drawer-section" style={{ marginTop: 24 }}>
                    <h4>Category Breakdown</h4>
                    {balanceSheet.categoryBreakdown.length === 0 && (
                      <p className="notes-text">No transactions in this period</p>
                    )}
                    {balanceSheet.categoryBreakdown.map((c, idx) => (
                      <div key={idx} className="category-breakdown-row">
                        <span className={`type-dot ${c.type === 'income' ? 'dot-income' : 'dot-expense'}`}></span>
                        <span className="category-name">{c.category}</span>
                        <span className="category-count">{c.cnt}x</span>
                        <strong className={c.type === 'income' ? 'stat-income-text' : 'stat-expense-text'}>
                          {c.type === 'income' ? '+' : '-'}{fmt(c.total)}
                        </strong>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="notes-text">Loading balance sheet...</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Finance;